import { type Api } from "api-client";
import {
  type ComponentType,
  createContext,
  type PropsWithChildren,
  type ReactNode,
  useContext,
} from "react";

type WorkoutItem = Api.GetWorkout.Output.WorkoutItem;
type ExerciseAttribute = NonNullable<
  WorkoutItem["exercise"]
>["exerciseAttributes"][number];
type WorkoutSet = WorkoutItem["workoutSets"][number];
type AttributeValue = WorkoutSet["attributeValues"][number];

export interface WorkoutItemSetContextValue {
  renderAssignedValue: () => ReactNode;
  updateEditableValue: (value: string) => void;
  getEditableValue: () => string;
  getEditableInputId: () => string;
  isEditableInputDisabled: () => boolean;
  getType: () => ExerciseAttribute["exerciseAttribute"]["type"];
}

export const workoutItemSetContext = createContext<WorkoutItemSetContextValue>(
  null!,
);

export function useWorkoutItemSetContext() {
  return useContext(workoutItemSetContext);
}

/**
 * Triggers a debounced persistence of the workout item's sets. Call this from
 * user-driven change handlers so hydration does not trigger spurious writes.
 */
export const workoutItemSaveContext = createContext<() => void>(() => {});

export function useWorkoutItemSave() {
  return useContext(workoutItemSaveContext);
}

export interface WorkoutItemSetProviderProps extends PropsWithChildren {
  attribute: ExerciseAttribute;
  workoutSet: WorkoutSet;
  attributeValue?: AttributeValue;
}

export interface LogCellProps {
  setId: string;
}

export interface AssignedValueSnapshot {
  workoutSetId: string;
  attributeValues: Array<{
    id: string;
    assignedValue: string;
  }>;
}

export interface LogWorkoutProgressSnapshot {
  workoutSetId: string;
  completed: boolean;
  attributeValues: Array<{
    id: string;
    trackedValue: string;
  }>;
}

export interface WorkoutItemSetsContextValue {
  Provider: ComponentType<WorkoutItemSetProviderProps>;
  getLogCells: () => [
    head: ComponentType,
    bodyCell: ComponentType<LogCellProps>,
  ];
}

export const workoutSetsWorkoutItemSetsContext =
  createContext<WorkoutItemSetsContextValue>(null!);

export function useWorkoutSetsWorkoutItemSetsContext() {
  return useContext(workoutSetsWorkoutItemSetsContext);
}
